import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { INDDNotification, INDDNotificationGrouped, NDDNotificationI18nService, NDDNotificationService, NDDNotificationStatus } from '@ndk/ng-notification';
import { NDDTranslationService } from '@ndk/ng-translation';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ICoreConfigInternal } from '@core/core.config.model';
import { IDownloadResponse } from '@core/download/download.model';
import { DownloadService } from '@core/download/download.service';

import { NotificationType } from './notification.model';

@Injectable()
export class NotificationService extends NDDNotificationService {
    private readonly RESOURCE_EXPIRED: string = '360ExpiredLink';
    private readonly RESOURCE_ERROR: string = '360ExportError';
    private apiUrl: string;

    constructor(
        @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal,
        private http: HttpClient,
        i18nService: NDDNotificationI18nService,
        private translationService: NDDTranslationService,
        private downloadService: DownloadService,
    ) {
        super(i18nService, translationService);
        this.apiUrl = `${ config.apiEndpoint }api/notifications`;
    }

    public getAll(): Observable<INDDNotificationGrouped[]> {
        return this.http.get(this.apiUrl)
            .pipe(
                map((response: INDDNotification[]) => this.handleNotifications(response)),
                map((response: INDDNotification[]) => this.groupNotifications(response)),
            );
    }

    public updateStatus(command: { ids: number[], status: NDDNotificationStatus }): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/update-status`, command);
    }

    public delete(command: { ids: number[] }): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/remove`, command);
    }

    public getNumberOfNewNotifications(): Observable<number> {
        return this.http.get<number>(`${ this.apiUrl }/counter`);
    }

    public download(id: number): Observable<IDownloadResponse> {
        return this.downloadService.download(`${ this.apiUrl }/download`, { id });
    }

    private handleNotifications(notifications: INDDNotification[]): INDDNotification[] {
        notifications.forEach((notification: INDDNotification) => {
            switch (notification.type) {
                case NotificationType.ExportUsers:
                    notification.icon = 'ndd-font ndd-font-users';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportPrinters:
                    notification.icon = 'ndd-font ndd-font-printer';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportDomains:
                    notification.icon = 'ndd-font ndd-font-at';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportUsersGroups:
                    notification.icon = 'ndd-font ndd-font-users';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportPrintersGroups:
                    notification.icon = 'ndd-font ndd-font-printer';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportMachines:
                    notification.icon = 'ndd-font ndd-font-machine';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportMachinesQueues:
                    notification.icon = 'ndd-font ndd-font-machine';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportPolicies:
                    notification.icon = 'ndd-font ndd-font-policies';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportReportPrintersWithLastAccounting:
                    notification.icon = 'ndd-font ndd-font-report-printer-notification';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportReportCardCodes:
                    notification.icon = 'ndd-font ndd-font-report-card-codes-notification';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.QuotaPaymentPaid:
                    notification.icon = 'ndd-font ndd-font-payment-ok-notification';
                    notification.emitEvent = false;
                    notification.description = this.translateDescription(notification.description);
                    break;
                case NotificationType.QuotaPaymentCancelled:
                    notification.icon = 'ndd-font ndd-font-payment-canceled-notification';
                    notification.emitEvent = false;
                    notification.description = this.translateDescription(notification.description);
                    break;
                case NotificationType.DeletedUser:
                    notification.icon = 'ndd-font ndd-font-user-o';
                    notification.emitEvent = false;
                    notification.description = this.translateDescription(notification.description);
                    break;
                case NotificationType.ExportReportSaveTonerEconomy:
                    notification.icon = 'ndd-font ndd-font-save-toner-economy-notification';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.ExportReportSaveTonerComparative:
                    notification.icon = 'ndd-font ndd-font-save-toner-comparative-notification';
                    this.changeNotificatonEventBehavior(notification);
                    break;
                case NotificationType.DeletedPrinter:
                    notification.icon = 'ndd-font ndd-font-printer';
                    notification.emitEvent = false;
                    notification.description = this.translateDescription(notification.description);
                    break;
                default:
                    break;
            }
        });

        return notifications;
    }

    private changeNotificatonEventBehavior(notification: INDDNotification): void {
        const isEvent: boolean = notification.description !== this.RESOURCE_EXPIRED && notification.description !== this.RESOURCE_ERROR;

        notification.emitEvent = isEvent;
        notification.markAsReadOnEmit = isEvent;
    }

    private translateDescription(description: string): string {
        const split: any = description.split('¬');

        return `${ this.translationService.instant(split[0]) }: ${ split[1] }`;
    }
}
